import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { Sparkles, Check, ArrowRight } from 'lucide-react';

/**
 * The slanted "1-Year Premium · ¥0 · No Credit Card" hero sticker, with
 * a viewport-anchored hover card.
 *
 * Iter 135 — customer-requested behavior change: the green "Welcome to
 * Early Access" pop-out must stay fully visible on screen no matter where
 * the user has scrolled to. The card sits in a `position: fixed` portal
 * vertically centered in the viewport, so scrolling up or down never
 * hides it. Horizontally it follows the trigger's right edge so the
 * visual relationship to the sticker is preserved.
 *
 * Hover behavior:
 *  - Open on mouseenter/focus on the sticker or the card itself.
 *  - 140 ms close delay so the cursor can travel from sticker → card
 *    without dismissing it.
 *  - Esc / blur closes immediately.
 *  - Mobile (touch) and screens narrower than `lg` hide the card entirely
 *    (the sticker is itself the link) — same behavior as the old radix
 *    HoverCard which was `hidden lg:block`.
 *
 * The popout content (sparkles, gradient, 4 perks bullet list) is
 * identical to the prior radix HoverCardContent — only the positioning
 * machinery changed.
 */
const PERKS = [
  { en: '1-Year Premium, free — no card, no risk.',         ja: '1年間プレミアム無料 — カード登録なし、リスクなし。' },
  { en: 'No automatic billing — paid upgrades are optional.',  ja: '自動課金なし — 有料プランは任意です。' },
  { en: 'Direct line to the team — your feedback shapes us.', ja: 'チームと直接つながれる — あなたの声が製品を作ります。' },
  { en: 'New features ship all year — your requests first.', ja: '新機能を年間通して追加。あなたの要望を最優先で実現。' },
];

const CARD_WIDTH = 600;          // matches old w-[600px]
const HORIZ_GAP = 20;            // pixels of breathing room between sticker and card
const VIEWPORT_PADDING = 24;     // keep card off the absolute screen edges

export default function StickyEarlyAccessSticker({ ja, prefix }) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({ left: -9999, top: -9999, visible: false });
  const triggerRef = useRef(null);
  const closeTimerRef = useRef(null);

  /** Recompute the card's viewport-fixed left/top from the trigger rect.
   *  Top is locked to viewport center; left follows the trigger horizontally
   *  with a small gap so the visual link is preserved during scroll. */
  const recompute = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    // Horizontal: prefer placing the card to the RIGHT of the trigger.
    let left = r.right + HORIZ_GAP;
    // If it would overflow the right edge, place it to the LEFT instead.
    if (left + CARD_WIDTH > vw - VIEWPORT_PADDING) {
      left = r.left - CARD_WIDTH - HORIZ_GAP;
    }
    // Clamp inside the viewport (small screens / edge cases).
    left = Math.max(VIEWPORT_PADDING, Math.min(left, vw - CARD_WIDTH - VIEWPORT_PADDING));

    // Vertical: anchor to viewport center so the card stays visible while
    // the trigger scrolls up/down. translateY(-50%) is applied via CSS.
    const top = vh / 2;

    setPosition({ left, top, visible: true });
  }, []);

  // Keep position in sync with scroll / resize while the card is open.
  useEffect(() => {
    if (!open) return;
    recompute();
    const onScrollOrResize = () => recompute();
    window.addEventListener('scroll', onScrollOrResize, { passive: true });
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open, recompute]);

  // Esc to close.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const cancelClose = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };
  const scheduleClose = () => {
    cancelClose();
    closeTimerRef.current = setTimeout(() => setOpen(false), 140);
  };
  const handleOpen = () => {
    cancelClose();
    setOpen(true);
  };

  return (
    <>
      <Link
        to={`${prefix}/free-trial`}
        className="group block focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 rounded-full"
        data-testid="hero-founder-sticker-link"
        aria-label={ja ? '1年間無料でプレミアム体験を始める' : 'Start the 1-Year Premium Trial'}
        ref={triggerRef}
        onMouseEnter={handleOpen}
        onMouseLeave={scheduleClose}
        onFocus={handleOpen}
        onBlur={scheduleClose}
      >
        <div className="relative" aria-hidden="true">
          <div className="relative w-64 h-64 sm:w-72 sm:h-72 -rotate-6 rounded-full bg-emerald-500 shadow-2xl shadow-emerald-600/40 ring-4 ring-emerald-900/10 group-hover:scale-[1.03] transition-transform">
            <div className="absolute inset-3 rounded-full border-2 border-dashed border-white/50" />
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-5">
              <span className="text-[10px] sm:text-xs font-black uppercase tracking-[0.26em] text-emerald-100 leading-tight">
                {ja ? '1年間プレミアム' : '1-Year Premium'}
              </span>
              <span className="mt-2 text-6xl sm:text-7xl font-black text-white leading-none tabular-nums">¥0</span>
              <span className="mt-2 text-[10px] sm:text-xs font-black uppercase tracking-[0.22em] text-emerald-50">
                {ja ? 'クレジットカード登録不要' : 'No credit card'}
              </span>
            </div>
          </div>
          <div className="absolute -top-3 -right-3 sm:-top-4 sm:-right-4 px-3 py-1.5 rounded-full bg-amber-400 text-slate-900 text-[10px] sm:text-xs font-black uppercase tracking-[0.18em] rotate-6 shadow-lg" data-testid="hero-founder-stamp">
            {ja ? '先行利用枠' : 'Early Access Spots'}
          </div>
        </div>
      </Link>

      {/* Viewport-fixed portal — only on lg+ screens (matches old behavior). */}
      {open && position.visible && createPortal(
        <div
          className="hidden lg:block fixed z-[90] pointer-events-auto transition-opacity duration-150"
          style={{
            left: position.left,
            top: position.top,
            width: CARD_WIDTH,
            transform: 'translateY(-50%)',
          }}
          data-testid="hero-founder-hovercard"
          role="dialog"
          aria-label={ja ? '先行利用の特典' : 'Early Access perks'}
          onMouseEnter={handleOpen}
          onMouseLeave={scheduleClose}
        >
          <Link
            to={`${prefix}/free-trial`}
            className="block focus:outline-none animate-in fade-in-0 zoom-in-95 duration-200"
            data-testid="hero-founder-hovercard-link"
          >
            <div
              className="relative rounded-3xl overflow-hidden shadow-2xl shadow-emerald-600/40 ring-1 ring-emerald-900/10 transition-transform hover:scale-[1.01]"
              style={{ transform: 'rotate(-6deg)' }}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-emerald-500 via-emerald-600 to-emerald-700" aria-hidden="true" />
              <div className="absolute -top-2 -left-2 text-amber-300 opacity-70 animate-pulse" aria-hidden="true">
                <Sparkles className="w-9 h-9" strokeWidth={2.2} />
              </div>
              <div className="absolute -top-1 right-5 text-amber-200 opacity-60 animate-pulse" style={{ animationDelay: '0.4s' }} aria-hidden="true">
                <Sparkles className="w-5 h-5" strokeWidth={2.2} />
              </div>
              <div className="absolute bottom-3 -right-1 text-amber-300 opacity-70 animate-pulse" style={{ animationDelay: '0.8s' }} aria-hidden="true">
                <Sparkles className="w-7 h-7" strokeWidth={2.2} />
              </div>

              <div className="relative px-7 pt-6 pb-6">
                <div className="flex items-center gap-3 mb-4">
                  <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-amber-400 text-slate-900 shadow-md">
                    <Sparkles className="w-6 h-6" strokeWidth={2.4} />
                  </span>
                  <div>
                    <p className="text-[11px] font-black uppercase tracking-[0.22em] text-amber-200">
                      {ja ? '限定特典' : 'Exclusive perk'}
                    </p>
                    <h4 className="text-2xl lg:text-[26px] font-black text-white leading-tight">
                      {ja ? '先行利用へようこそ' : 'Welcome to Early Access'}
                    </h4>
                  </div>
                </div>

                <p className="text-base text-emerald-50 leading-snug mb-4 [text-wrap:balance]">
                  {ja
                    ? 'JTRの新サービスをいち早くご利用いただく先行利用者の方だけに、特別な特典をご用意しました。'
                    : 'A thank-you to our early-access members — special perks for being among the first to use JTR.'}
                </p>

                <ul className="space-y-2.5 mb-5">
                  {PERKS.map((b, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-base text-white leading-snug font-semibold">
                      <span className="flex-shrink-0 inline-flex items-center justify-center w-5 h-5 rounded-full bg-amber-400 text-slate-900 mt-0.5">
                        <Check className="w-3 h-3" strokeWidth={3.4} />
                      </span>
                      <span className="whitespace-nowrap">{ja ? b.ja : b.en}</span>
                    </li>
                  ))}
                </ul>

                <div className="flex items-center justify-between gap-3 pt-4 border-t border-white/20">
                  <p className="text-xs font-black uppercase tracking-[0.18em] text-amber-200">
                    {ja ? '特典を受け取る' : 'Claim now'}
                  </p>
                  <span className="inline-flex items-center gap-1.5 text-sm font-black uppercase tracking-wider text-white">
                    {ja ? '無料体験を始める' : 'Start the free trial'}
                    <ArrowRight className="w-4 h-4" strokeWidth={2.6} />
                  </span>
                </div>
              </div>
            </div>
          </Link>
        </div>,
        document.body
      )}
    </>
  );
}
